import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { ModalHeader } from '../../components/ModalHeader';

import {
  Container,
  ContainerDataSubscription,
  ContainerInfo,
  Content,
  DataSubscription,
  Info,
  Select,
  Submit,
} from './styles';

import { Icon } from '../../components/Icon';
import { colors } from '../../styles/tokens';
import { dueDates } from '../../objects/select';
import { useAuth } from '../../hooks/auth';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { getValidationErrors } from '../../utils/getValidationErrors';
import { api } from '../../services/api';

interface modalProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  news: number | null;
  exercises: number | null;
  users: number | null;
  price: number;
  subscription_id: string;
  onSuccess?: () => void;
}

const capLabel = (value: number | null): string =>
  value === null ? 'Ilimitado' : String(value);

interface formProps {
  due_date: number;
}

const SubscriptionData: React.FC<modalProps> = ({
  title,
  news,
  exercises,
  users,
  price,
  subscription_id,
  onSuccess,
  ...rest
}) => {
  const formRef = useRef<FormHandles>(null);
  const { removeAllModals } = useModal();
  const { addToast } = useToast();
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    api
      .get('subscriptions/current')
      .then(() => {
        setIsSubscribed(true);
      })
      .catch(() => {
        setIsSubscribed(false);
      });
  }, []);

  const handleSubmit = useCallback(
    async (data: formProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          due_date: yup
            .number()
            .required()
            .test(
              'test',
              'Data não disponível.',
              (value: number | undefined) =>
                value === 5 || value === 10 || value === 15,
            ),
        });

        await schema.validate(data, { abortEarly: false });

        if (!isSubscribed) {
          await api.post('subscriptions/subscribe', {
            due_date_at: data.due_date,
            subscription_id,
          });
        } else {
          await api.put('subscriptions/subscribe', {
            due_date_at: data.due_date,
            subscription_id,
          });
        }

        const userData = await api.get('users');
        updateUser(userData.data);

        addToast({
          type: 'success',
          title: 'Assinatura realizada com sucesso.',
          description: 'Acesse a fatura gerada para escolher a forma de pagamento.',
        });

        onSuccess?.();

        removeAllModals();
        navigate('/gym/payments');
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }

        if (err?.response?.status === 401 || err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Houve um erro. Tente novamente.',
        });
      }
    },
    [
      addToast,
      isSubscribed,
      navigate,
      removeAllModals,
      subscription_id,
      onSuccess,
      updateUser,
    ],
  );

  return (
    <Container {...rest}>
      <ModalHeader title="Dados da assinatura" />
      <ContainerInfo>
        <Info text="Escolha o dia do vencimento da fatura." />
      </ContainerInfo>

      <Content ref={formRef} onSubmit={handleSubmit}>
        <h2>Plano {title}</h2>

        <ContainerDataSubscription>
          <DataSubscription>
            <div>
              <Icon name="warning" size={20} color={colors.onSurfaceVariant} />
              <span>Alertas: </span>
            </div>
            <span>{capLabel(news)}</span>
          </DataSubscription>
          <DataSubscription>
            <div>
              <Icon
                name="fitness_center"
                size={20}
                color={colors.onSurfaceVariant}
              />
              <span>Exercícios: </span>
            </div>
            <span>{capLabel(exercises)}</span>
          </DataSubscription>
          <DataSubscription>
            <div>
              <Icon name="group" size={20} color={colors.onSurfaceVariant} />
              <span>Usuários: </span>
            </div>
            <span>{capLabel(users)}</span>
          </DataSubscription>
          <DataSubscription>
            <div>
              <Icon name="event" size={20} color={colors.onSurfaceVariant} />
              <span>Vencimento da fatura: </span>
            </div>
            <Select
              name="due_date"
              options={dueDates}
              defaultValue={{ value: 10, label: '10' }}
              isFlex1Mobile
            />
          </DataSubscription>
          <DataSubscription>
            <div>
              <Icon name="payments" size={20} color={colors.onSurfaceVariant} />
              <span>Preço: </span>
            </div>
            <span>{`R$ ${price.toFixed(2)}`}</span>
          </DataSubscription>
        </ContainerDataSubscription>
        <Submit
          name="submit"
          value="Confirmar"
          onClick={formRef.current?.submitForm}
        />
      </Content>
    </Container>
  );
};

export { SubscriptionData };
